import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormErrorMessage, Input, Select, Textarea, formatHookFormErrors } from '@dashboard/ui'
import { ButtonIcon, FieldGroup, FieldSection, Header, ImageUploader, SelectDateTime } from '@/ui'
import { CloseOutline } from '@/icons'
import { S3Bucket } from '@/types'
import { MAX_DESCRIPTION_LENGTH, MAX_NAME_LENGTH } from '@/Settings'
import { DisplayLevelType, ProgressionProgramRead } from '@/api/dashboard'
import { ErrorMessage } from '@hookform/error-message'
import { SelectSkuTable } from '@/layouts/components/SelectSkuTable'
import { validateInterval } from '@/layouts/components/ItemTimelimit'

interface RewardProgramSettingsFormProps {
  onClose: () => void
  program: ProgressionProgramRead
  onSubmit: (data: ProgressionProgramRead) => void
}

export const ProgressionProgramSettingsForm = ({ onClose, program, onSubmit }: RewardProgramSettingsFormProps) => {
  const { t } = useTranslation()
  const {
    register,
    control,
    clearErrors,
    handleSubmit,
    setError,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<ProgressionProgramRead>({
    values: {
      ...program,
    },
  })

  const onSubmitLocal = async (data: ProgressionProgramRead) => {
    const err = validateInterval(data.start_at, data.end_at, t)
    if (err) {
      setError('start_at', { message: err })
      return
    } else {
      clearErrors('start_at')
    }
    onSubmit(data)
    onClose()
  }

  const main_item_id = watch('main_item_id')

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmitLocal)}>
      <Header
        title={<span className="font-nohemi text-paragraph-md font-medium">{t('settings')}</span>}
        minWidth="auto"
        className="p-4"
      >
        <div className="flex gap-4">
          <Button variant="default" type="submit">
            {t('apply')}
          </Button>
          <ButtonIcon
            onClick={() => {
              onClose()
            }}
            type="button"
            size="sm"
            variant="outline"
            color="secondary"
          >
            <CloseOutline />
          </ButtonIcon>
        </div>
      </Header>

      <div className="flex flex-col gap-4 px-4">
        <FieldSection label={t('general')}>
          <FieldGroup label={<span className="text-caption-sm font-normal text-text-tertiary">{t('name')}</span>}>
            <Input
              disabled={isSubmitting}
              {...formatHookFormErrors(errors, 'name')}
              {...register('name', {
                required: t('validation.required'),
                validate: value => (value && value.trim().length > 0) || t('validation.required'),
                maxLength: {
                  value: MAX_NAME_LENGTH,
                  message: t('loyalty.summary.name.validation', { max: MAX_NAME_LENGTH }),
                },
              })}
              {...formatHookFormErrors(errors, 'name')}
            />
          </FieldGroup>

          <FieldGroup label={t('progression.title')}>
            <Input disabled={isSubmitting} {...formatHookFormErrors(errors, 'title')} {...register('title')} />
          </FieldGroup>

          <FieldGroup label={t('progression.content')}>
            <Input disabled={isSubmitting} {...formatHookFormErrors(errors, 'content')} {...register('content')} />
            <Input disabled={isSubmitting} {...register('title')} {...formatHookFormErrors(errors, 'title')} />
          </FieldGroup>

          <FieldGroup label={t('progression.content')}>
            <Input disabled={isSubmitting} {...register('content')} {...formatHookFormErrors(errors, 'content')} />
          </FieldGroup>

          <FieldGroup label={t('description')}>
            <Textarea
              size="md"
              rows={3}
              disabled={isSubmitting}
              {...register('description', {
                maxLength: {
                  value: MAX_DESCRIPTION_LENGTH,
                  message: t('loyalty.summary.description.validation', { max: MAX_DESCRIPTION_LENGTH }),
                },
              })}
              {...formatHookFormErrors(errors, 'description')}
            />
          </FieldGroup>

          <FieldGroup label={t('background-image')}>
            <Controller
              name="settings.background_image_url"
              control={control}
              render={({ field }) => (
                <ImageUploader
                  {...field}
                  accept={{
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/png': [],
                    'image/webp': [],
                    'image/gif': [],
                  }}
                  bucket={S3Bucket.news}
                />
              )}
            />
          </FieldGroup>

          <FieldGroup label={t('background_image.size')}>
            <Controller
              control={control}
              name="settings.background_size"
              render={({ field }) => (
                <Select
                  {...field}
                  {...formatHookFormErrors(errors, 'settings.background_size')}
                  items={[
                    {
                      value: 'cover',
                      children: t('background_image.size.cover'),
                    },
                    {
                      value: 'repeat',
                      children: t('background_image.size.repeat'),
                    },
                  ]}
                />
              )}
            />
            <ErrorMessage
              name="settings.background_size"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>

          <FieldGroup label={t('progression.display_level_type')}>
            <Controller
              control={control}
              name="settings.display_level_type"
              render={({ field }) => (
                <Select
                  {...field}
                  {...formatHookFormErrors(errors, 'settings.display_level_type')}
                  items={Object.values(DisplayLevelType).map(it => ({
                    value: it,
                    children: t(`progression.display_level_type.${it}`),
                  }))}
                />
              )}
            />
          </FieldGroup>
        </FieldSection>

        <div className="divider" />

        <FieldSection label={t('time-limitations')}>
          <FieldGroup label={t('datetime.start_at')}>
            <Controller control={control} name="start_at" render={({ field }) => <SelectDateTime {...field} />} />
            <ErrorMessage
              name="start_at"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>
          <FieldGroup label={t('datetime.end_at')}>
            <Controller control={control} name="end_at" render={({ field }) => <SelectDateTime {...field} />} />
            <ErrorMessage
              name="end_at"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>
        </FieldSection>

        <div className="divider" />

        <FieldSection
          label={t('progression.pp_granting_item_ids')}
          caption={t('progression.pp_granting_item_ids.desc')}
        >
          <FieldGroup
            label={t('progression.pp_granting_item_ids.select')}
            extraRight={<div className="ml-auto">{t('progression.main_item_id')}</div>}
          >
            <Controller
              name="pp_granting_item_ids"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <SelectSkuTable
                  mode="single"
                  isStackable={true}
                  onChange={v => {
                    clearErrors('pp_granting_item_ids')
                    field.onChange(v)

                    if (main_item_id && !v.includes(main_item_id)) {
                      setValue('main_item_id', null, { shouldDirty: true })
                    }
                  }}
                  value={field.value || []}
                  selectedValues={main_item_id ? [main_item_id] : []}
                  onSelectedValuesChange={v => {
                    if (v.length > 0) {
                      setValue('main_item_id', v[0], { shouldDirty: true })
                    } else {
                      setValue('main_item_id', null, { shouldDirty: true })
                    }
                  }}
                />
              )}
            />
            <ErrorMessage
              name="pp_granting_item_ids"
              errors={errors}
              render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
            />
          </FieldGroup>
        </FieldSection>
      </div>
    </form>
  )
}
